/**
 * ADR Update Event Service
 * Manages custom events for ADR updates to trigger timestamp reloads
 */

export const ADR_UPDATE_EVENT = 'adr-updated';

/**
 * Dispatches a custom event when an ADR is updated (definition or content).
 *
 * @param {number} projectId - The ID of the project containing the ADR.
 * @param {string} [adrId] - The optional ID of the ADR that was updated.
 * @param {string} [timestamp] - The optional timestamp of the update (defaults to current time).
 * Triggers a window event for listeners to reload timestamps or data.
 */
export const notifyAdrUpdated = (projectId: number, adrId?: string, timestamp?: string) => {
  const eventTimestamp = timestamp || new Date().toISOString();
  const event = new CustomEvent(ADR_UPDATE_EVENT, {
    detail: { projectId, adrId, timestamp: eventTimestamp },
  });
  globalThis.dispatchEvent(event);
};

/**
 * Registers a listener for ADR update events.
 *
 * @param {(projectId: number, adrId?: string, actionTimestamp?: string) => void} callback - Function called when an ADR update event occurs.
 *   - projectId: The ID of the project whose ADR was updated.
 *   - adrId: The ID of the updated ADR (optional).
 *   - actionTimestamp: The ISO timestamp of when the action occurred (optional).
 * @returns {() => void} Cleanup function to remove the event listener.
 *
 * @example
 * // Listen for ADR updates and log them
 * });
 * // Later, to remove the listener:
 * cleanup();
 */
export const onAdrUpdated = (callback: (projectId: number,adrId?: string, actionTimestamp?: string) => void) => {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ projectId: number; adrId?: string; timestamp?: string }>;
    callback(customEvent.detail.projectId, customEvent.detail.adrId, customEvent.detail.timestamp);
  };
  
  globalThis.addEventListener(ADR_UPDATE_EVENT, handler);
  return () => globalThis.removeEventListener(ADR_UPDATE_EVENT, handler);
};
